import { Injectable } from '@angular/core';
import { AddressSuggestion, ResolvedAddress } from 'app/core/models';

// Google ends an autocomplete session a few minutes after it starts; a reused stale token is billed per request.
const SESSION_TOKEN_LIFETIME_MS = 3 * 60 * 1000;

// Only street-level places can fill Address Line 1; ZIP codes, cities and businesses resolve to nothing.
const STREET_ADDRESS_TYPES = ['street_address', 'premise', 'subpremise'];

// Google Places Autocomplete (New). Suggestion requests and the final details lookup
// share one session token so Google bills them as a single session.
@Injectable({ providedIn: 'root' })
export class AddressAutocompleteService {
  private _sessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  private _sessionStartedAt = 0;

  /** Returns US address predictions for the typed text, or an empty list when Places is unavailable. */
  async suggest(input: string): Promise<AddressSuggestion[]> {
    try {
      const { AutocompleteSessionToken, AutocompleteSuggestion } = (await google.maps.importLibrary(
        'places'
      )) as google.maps.PlacesLibrary;
      if (this._sessionToken === null || Date.now() - this._sessionStartedAt > SESSION_TOKEN_LIFETIME_MS) {
        this._sessionToken = new AutocompleteSessionToken();
        this._sessionStartedAt = Date.now();
      }
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['us'],
        includedPrimaryTypes: STREET_ADDRESS_TYPES,
        sessionToken: this._sessionToken,
      });
      return suggestions.flatMap(({ placePrediction }) =>
        placePrediction
          ? [
              {
                text: placePrediction.text.text,
                mainText: placePrediction.mainText?.text ?? placePrediction.text.text,
                prediction: placePrediction,
              },
            ]
          : []
      );
    } catch (error) {
      console.warn('Address suggestions are unavailable.', error);
      return [];
    }
  }

  /** Loads the full address of a suggestion; null when it is not a complete US street address. */
  async resolve(suggestion: AddressSuggestion): Promise<ResolvedAddress | null> {
    try {
      const { place } = await suggestion.prediction
        .toPlace()
        .fetchFields({ fields: ['addressComponents', 'formattedAddress', 'location'] });
      const address = this.parseUsAddress(place.addressComponents ?? [], place.formattedAddress ?? suggestion.text);
      return address && { ...address, location: place.location?.toJSON() ?? null };
    } catch (error) {
      console.warn('Failed to load the selected address.', error);
      return null;
    } finally {
      this._sessionToken = null;
    }
  }

  // Only US addresses are supported by the onboarding form.
  private parseUsAddress(
    parts: google.maps.places.AddressComponent[],
    formattedAddress: string
  ): ResolvedAddress | null {
    const find = (type: string): google.maps.places.AddressComponent | undefined =>
      parts.find(part => part.types.includes(type));

    if (find('country')?.shortText !== 'US') {
      return null;
    }

    const streetNumber = find('street_number')?.longText;
    const route = find('route')?.longText;
    const city = (find('locality') ?? find('sublocality') ?? find('postal_town'))?.longText;
    const stateName = find('administrative_area_level_1')?.longText;
    const zipCode = find('postal_code')?.longText;

    if (!streetNumber || !route || !city || !stateName || !zipCode) {
      return null;
    }

    return { addressLine1: `${streetNumber} ${route}`, city, stateName, zipCode, formattedAddress };
  }
}
